import { uniq } from "lodash";

import { tryGetHexId } from "../humanHexIds.js";
import { HexId } from "../idTypeBrands.js";
import { HEX_PROJECT_TAG } from "../magic/inlineReferences.js";
import { notEmpty } from "../notEmpty.js";

export const GUIDE_MARKDOWN_MONACO_LANG_ID = "guide-markdown";

/**
 * Matches `@project <id>` mentions.
 * The id capture group accepts UUIDs, URL slugs, and compressed hex IDs.
 * Returns a fresh RegExp each call to avoid stale lastIndex state.
 */
export const getProjectMentionRegex = (): RegExp =>
  /@project\s+([a-zA-Z0-9-]+)/g;

export const getProjectMentionRegexForMonarch = (): RegExp =>
  // `@` has special meaning for monaco monarch languages,
  // so make a variant of the regex that escapes it.
  // See https://github.com/microsoft/vscode/blob/0a01b4f5ae8d31caa948826706da99696e9e77a6/src/vs/editor/standalone/common/monarch/monarchCompile.ts#L85
  // We also don't need a global regex for monaco monarch grammars.
  new RegExp(getProjectMentionRegex().source.replaceAll("@", "@@"));

/** Given a match from `getProjectMentionRegex()`, returns the hex ID it matches. */
export function parseProjectMentionMatch(
  match: RegExpMatchArray,
): HexId | null {
  const part = match[1];
  if (part == null) return null;

  // Eventually we might want to support matching an ID from a URL here too
  return tryGetHexId(part);
}

/**
 * Extracts unique hex project IDs from `@project <hexId>` mentions.
 * `hexId` can be a full hex UUID or a human hex id that we use in URLs.
 */
export function extractProjectMentionIds(text: string): HexId[] {
  const matches = [...text.matchAll(getProjectMentionRegex())];
  const hexIds = matches.map(parseProjectMentionMatch).filter(notEmpty);
  return uniq(hexIds);
}

/**
 * Replaces `@project <id>` mentions with `[hexProject:<shortRef>]` tags.
 *
 * Mentions whose UUID is not present in `refMap` are left unchanged.
 */
export function substituteProjectMentions(
  text: string,
  refMap: Map<HexId, { shortRef: string }>,
): string {
  if (refMap.size === 0) {
    return text;
  }

  return text.replace(getProjectMentionRegex(), (match, rawId: string) => {
    const hexId = tryGetHexId(rawId);
    if (hexId == null) return match;

    const entry = refMap.get(hexId);
    if (!entry) return match;

    return `[${HEX_PROJECT_TAG}:${entry.shortRef}]`;
  });
}

export function substituteProjectMentionsWithFullIdShortRefs(
  text: string,
): string {
  const hexIds = extractProjectMentionIds(text);
  const refMap = new Map(hexIds.map((hexId) => [hexId, { shortRef: hexId }]));
  return substituteProjectMentions(text, refMap);
}
